import { createColumnHelper } from '@tanstack/react-table'
import { StyleSheet, css } from 'aphrodite'
import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import ActionMenu from '../components/ActionMenu'
import DateRangeFilter from '../components/DateRangeFilter'
import EntitySelectorModal from '../components/EntitySelectorModal'
import NotesPreview from '../components/NotesPreview'
import OpenMap from '../components/OpenMaps'
import PhoneContact from '../components/PhoneContact'
import SearchBar from '../components/SearchBar'
import SkeletonLoader from '../components/SkeletonLoader'
import Table from '../components/dashboard/Table'
import StatusReportConfirmationModal from '../components/report/StatusReportConfirmationModal'
import AddTagModal, { TAG_DRAFT_KEY } from '../components/tag/AddTagModal'
import TagSubRow from '../components/tag/TagSubRow'
import AcceptWorkOrderModal from '../components/workorder/AcceptWorkOrderModal'
import AddWorkOrderModal, {
  WORKORDER_DRAFT_KEY,
} from '../components/workorder/AddWorkOrderModal'
import DispatchTemplateSelectorModal from '../components/workorder/DispatchTemplateSelectorModal'
import ProjectSelectorItem from '../components/workorder/ProjectSelectorItem'
import ViewWorkOrderModal from '../components/workorder/ViewWorkOrderModal'
import type { ProjectTemplateRecipientType } from '../config/templateConfig'
import { useNotificationContext } from '../contexts/NotificationContext'
import { useTagContext } from '../contexts/TagContext'
import { useUser } from '../contexts/UserContext'
import { useWorkOrderContext } from '../contexts/WorkOrderContext'
import { projectService } from '../services/projectService'
import { statusReportService } from '../services/statusReportService'
import { userService } from '../services/userService'
import {
  type WorkOrderFilters,
  workOrderService,
} from '../services/workOrderService'
import type { Project } from '../types/project'
import { ProjectStatus } from '../types/project'
import type { Tag } from '../types/tag'
import type { Contact } from '../types/user'
import { StakeholderType, UserType } from '../types/user'
import type { WorkOrder, WorkOrderStatus } from '../types/workorder'
import { getTruckTypeLabel } from '../types/workorder'
import { formatDate } from '../utils/date'

// Helper function to format date and time together from datetime string
const formatDateTimeFromString = (
  dateTimeString: string | null | undefined,
): string => {
  if (!dateTimeString) return '-'
  try {
    const date = new Date(dateTimeString)

    // Format date part
    const dateFormatted = formatDate(dateTimeString)

    // Format time part
    let hours = date.getHours()
    const minutes = date.getMinutes()
    const period = hours >= 12 ? 'PM' : 'AM'

    // Convert to 12-hour format
    if (hours > 12) {
      hours = hours - 12
    } else if (hours === 0) {
      hours = 12
    }

    const timeFormatted = `${hours}:${minutes
      .toString()
      .padStart(2, '0')} ${period}`

    return `${dateFormatted} ${timeFormatted}`
  } catch (error) {
    console.error('Error formatting date/time:', error)
    return '-'
  }
}

const WorkOrderDashboard: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useUser()
  const { refreshWorkOrders } = useWorkOrderContext()
  const { workOrders: workOrderNotifications, refresh: refreshNotifications } =
    useNotificationContext()
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([])
  const { tags } = useTagContext()
  const [contacts, setContacts] = useState<Contact[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedWorkOrders, setSelectedWorkOrders] = useState<Set<string>>(
    new Set(),
  )
  const [isReportMode, setIsReportMode] = useState(false)
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set())
  const [projectLabels, setProjectLabels] = useState<Record<string, string>>({})
  const [tagModalWorkOrder, setTagModalWorkOrder] = useState<WorkOrder | null>(
    null,
  )
  const [selectedAcceptWorkOrder, setSelectedAcceptWorkOrder] =
    useState<WorkOrder | null>(null)
  const [selectedViewWorkOrder, setSelectedViewWorkOrder] =
    useState<WorkOrder | null>(null)
  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)
  const [searchValue, setSearchValue] = useState<string>('')
  const [activeSearch, setActiveSearch] = useState<string>('')
  const [showStatusReportConfirmation, setShowStatusReportConfirmation] =
    useState(false)
  const [selectedTagsForReport, setSelectedTagsForReport] = useState<Tag[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined)
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined)

  // New Dispatch flow state
  const [showProjectSelector, setShowProjectSelector] = useState(false)
  const [acceptedProjects, setAcceptedProjects] = useState<Project[]>([])
  const [isLoadingProjects, setIsLoadingProjects] = useState(false)
  const [projectSearchValue, setProjectSearchValue] = useState('')
  const [pendingDispatchProject, setPendingDispatchProject] =
    useState<Project | null>(null)
  const [selectedDispatchTemplateType, setSelectedDispatchTemplateType] =
    useState<ProjectTemplateRecipientType | null>(null)
  const [projectSelectorPage, setProjectSelectorPage] = useState(1)
  const [projectSelectorTotalPages, setProjectSelectorTotalPages] = useState(1)
  const [projectSelectorTotalCount, setProjectSelectorTotalCount] = useState(0)
  const projectSelectorPageSize = 5

  const currentTab = location.pathname.includes('drafts')
    ? 'drafts'
    : location.pathname.includes('completed')
      ? 'completed'
      : location.pathname.includes('accepted')
        ? 'accepted'
        : location.pathname.includes('declined')
          ? 'declined'
          : 'pending'

  const safeWorkOrders = Array.isArray(workOrders) ? workOrders : []
  const filteredWorkOrders = safeWorkOrders.filter((workOrder) => {
    if (currentTab === 'drafts') {
      return workOrder.status === 'DRAFT'
    } else if (currentTab === 'accepted') {
      return workOrder.status === 'ACCEPTED'
    } else if (currentTab === 'completed') {
      return workOrder.status === 'COMPLETE'
    } else if (currentTab === 'declined') {
      return workOrder.status === 'DECLINED'
    } else {
      return (
        workOrder.status === 'PENDING' ||
        workOrder.status === 'NEW' ||
        !workOrder.status
      )
    }
  })

  const workOrdersToDisplay =
    filteredWorkOrders.length > 0 ? filteredWorkOrders : safeWorkOrders

  const fetchWorkOrders = useCallback(async () => {
    try {
      setIsLoading(true)
      const filters: WorkOrderFilters & { page?: number; page_size?: number } =
        {}

      if (dateFrom) filters.dateFrom = dateFrom
      if (dateTo) filters.dateTo = dateTo
      if (activeSearch.trim()) filters.search = activeSearch.trim()
      // Send status filter to backend for correct pagination counts
      if (currentTab === 'drafts') filters.status = 'DRAFT' as WorkOrderStatus
      else if (currentTab === 'accepted')
        filters.status = 'ACCEPTED' as WorkOrderStatus
      else if (currentTab === 'completed')
        filters.status = 'COMPLETE' as WorkOrderStatus
      else if (currentTab === 'declined')
        filters.status = 'DECLINED' as WorkOrderStatus
      else filters.status = 'PENDING' as WorkOrderStatus
      filters.page = page
      filters.page_size = pageSize

      const res = await workOrderService.getWorkOrders(filters)
      setWorkOrders(Array.isArray(res.items) ? res.items : [])
      setTotalCount(res.count)
      setTotalPages(res.pages)
    } catch (error) {
      console.error('Failed to fetch work orders:', error)
    } finally {
      setIsLoading(false)
    }
  }, [dateFrom, dateTo, activeSearch, page, pageSize, currentTab])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const contactsData = await userService.getContacts()
        setContacts(contactsData)
      } catch (error) {
        console.error('Failed to fetch data:', error)
      }
    }

    fetchData()
  }, [])

  // Fetch work orders when date filters change
  useEffect(() => {
    fetchWorkOrders()
  }, [fetchWorkOrders])

  useEffect(() => {
    const loadProjectLabels = async () => {
      const labels: Record<string, string> = {}
      const orders = Array.isArray(workOrders) ? workOrders : []
      for (const order of orders) {
        try {
          // Extract project ID - handle both number and Project object
          let projectId: string
          if (typeof order.project === 'object') {
            projectId = order.project.id.toString()
          } else {
            projectId = order.project.toString()
          }

          const label = await projectService.getLabel(projectId)
          if (!label) {
            console.warn('No label returned for project:', projectId)
          }
          labels[projectId] = label || '-'
        } catch (error) {
          console.error(
            'Failed to get label for project:',
            typeof order.project === 'object'
              ? order.project.id
              : order.project,
            error,
          )
        }
      }
      setProjectLabels(labels)
    }
    loadProjectLabels()
  }, [workOrders])

  // Report mode selection logic
  const getTagsForWorkOrder = useCallback(
    (workOrderId: string) => {
      const safeTags = Array.isArray(tags) ? tags : []
      return safeTags.filter((tag) => {
        const tagWorkorderId =
          typeof tag.workorder === 'object' && tag.workorder
            ? (tag.workorder as { id: number }).id
            : tag.workorder
        return String(tagWorkorderId) === workOrderId
      })
    },
    [tags],
  )

  const handleWorkOrderToggle = useCallback(
    (workOrderId: string, checked: boolean) => {
      if (checked) {
        // Select all tags in this work order
        const workOrderTags = getTagsForWorkOrder(workOrderId)
        setSelectedTags(
          (prev) =>
            new Set([
              ...Array.from(prev),
              ...workOrderTags.map((t) => t.id.toString()),
            ]),
        )
        setSelectedWorkOrders(
          (prev) => new Set([...Array.from(prev), workOrderId]),
        )
      } else {
        // Deselect all tags in this work order
        const workOrderTags = getTagsForWorkOrder(workOrderId)
        setSelectedTags((prev) => {
          const newSet = new Set(prev)
          for (const tag of workOrderTags) {
            newSet.delete(tag.id.toString())
          }
          return newSet
        })
        setSelectedWorkOrders((prev) => {
          const newSet = new Set(prev)
          newSet.delete(workOrderId)
          return newSet
        })
      }
    },
    [getTagsForWorkOrder],
  )

  const handleTagToggle = useCallback((tagId: string, checked: boolean) => {
    if (checked) {
      setSelectedTags((prev) => new Set([...Array.from(prev), tagId]))
    } else {
      setSelectedTags((prev) => {
        const newSet = new Set(prev)
        newSet.delete(tagId)
        return newSet
      })
    }
  }, [])

  const handleGenerateReport = useCallback(() => {
    setIsReportMode(true)
    setSelectedTags(new Set())
    setSelectedWorkOrders(new Set())
  }, [])

  // Fetch accepted projects for the project selector (server-side pagination)
  const fetchAcceptedProjects = useCallback(
    async (page: number, search: string) => {
      setIsLoadingProjects(true)
      try {
        const response = await projectService.getProjects({
          status: ProjectStatus.ACCEPTED,
          page,
          page_size: projectSelectorPageSize,
          search: search.trim() || undefined,
        })
        setAcceptedProjects(response.items)
        setProjectSelectorTotalPages(response.pages || 1)
        setProjectSelectorTotalCount(response.count || 0)
      } catch (error) {
        console.error('Failed to fetch accepted projects:', error)
        toast.error('Failed to load project terms')
      } finally {
        setIsLoadingProjects(false)
      }
    },
    [],
  )

  // Handle opening the New Dispatch flow
  const handleNewDispatch = useCallback(() => {
    // Clear any existing draft when starting fresh
    localStorage.removeItem(WORKORDER_DRAFT_KEY)
    setProjectSearchValue('')
    setProjectSelectorPage(1)
    setShowProjectSelector(true)
    fetchAcceptedProjects(1, '')
  }, [fetchAcceptedProjects])

  // Handle project selection
  const handleProjectSelect = useCallback((project: Project) => {
    setShowProjectSelector(false)
    setPendingDispatchProject(project)
  }, [])

  // Handle template type selection
  const handleTemplateTypeSelect = useCallback(
    (templateType: ProjectTemplateRecipientType) => {
      // Clear any existing draft when starting fresh
      localStorage.removeItem(WORKORDER_DRAFT_KEY)
      setSelectedDispatchTemplateType(templateType)
    },
    [],
  )

  // Handle closing the dispatch flow
  const handleCloseDispatchFlow = useCallback(() => {
    setPendingDispatchProject(null)
    setSelectedDispatchTemplateType(null)
  }, [])

  // Handle project selector search change with debounce
  const handleProjectSearchChange = useCallback((value: string) => {
    setProjectSearchValue(value)
    setProjectSelectorPage(1) // Reset to page 1 on search
  }, [])

  // Handle project selector page change
  const handleProjectPageChange = useCallback(
    (newPage: number) => {
      setProjectSelectorPage(newPage)
      fetchAcceptedProjects(newPage, projectSearchValue)
    },
    [fetchAcceptedProjects, projectSearchValue],
  )

  // Fetch projects when search changes (with debounce)
  useEffect(() => {
    if (!showProjectSelector) return

    const timeoutId = setTimeout(() => {
      fetchAcceptedProjects(projectSelectorPage, projectSearchValue)
    }, 300) // 300ms debounce for search

    return () => clearTimeout(timeoutId)
  }, [
    showProjectSelector,
    projectSelectorPage,
    projectSearchValue,
    fetchAcceptedProjects,
  ])

  const handleCancelReport = useCallback(() => {
    setIsReportMode(false)
    setSelectedTags(new Set())
    setSelectedWorkOrders(new Set())
  }, [])

  const handleSubmitReport = useCallback(() => {
    if (selectedTags.size === 0) {
      toast.error('Please select at least one tag to generate a report')
      return
    }

    // Get the selected tags data
    const selectedTagsData = tags.filter((tag) =>
      selectedTags.has(String(tag.id)),
    )
    setSelectedTagsForReport(selectedTagsData)
    setShowStatusReportConfirmation(true)
  }, [selectedTags, tags])

  const handleConfirmStatusReport = useCallback(
    async (recipientUserIds: number[], notes: string) => {
      try {
        const tagIds = Array.from(selectedTags)
        const { report, statusReportId } =
          await statusReportService.generateReport({
            tag_ids: tagIds.map((id) => Number.parseInt(id)),
            recipient_user_ids:
              recipientUserIds.length > 0 ? recipientUserIds : null,
            notes: notes || null,
          })

        // Debug: Log the report structure to understand what we're getting
        console.log('Generated report:', report)
        console.log('Status report ID:', statusReportId)

        // Navigate to TagReportPage with the status report ID
        navigate(`/tag-report/${statusReportId}`)

        // Close the modal and reset state
        setShowStatusReportConfirmation(false)
        setIsReportMode(false)
        setSelectedTags(new Set())
        setSelectedWorkOrders(new Set())
      } catch (error) {
        console.error('Failed to generate report:', error)

        // Handle different error types
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { status?: number } }
          if (axiosError.response?.status === 405) {
            toast.error(
              'Report generation endpoint is not available yet. Please contact support.',
            )
          } else if (axiosError.response?.status === 403) {
            toast.error(
              'You do not have permission to generate reports. Please contact your administrator.',
            )
          } else if (axiosError.response?.status === 404) {
            toast.error(
              'Report generation service is not available. Please contact support.',
            )
          } else {
            toast.error('Failed to generate report. Please try again later.')
          }
        } else {
          toast.error('Failed to generate report. Please try again later.')
        }
      }
    },
    [selectedTags, navigate],
  )

  const columnHelper = createColumnHelper<WorkOrder>()

  // Sort workorders by created_date
  const sortedWorkOrders = [...workOrdersToDisplay].sort((a, b) => {
    const dateA = new Date(a.created_date || '')
    const dateB = new Date(b.created_date || '')
    return dateB.getTime() - dateA.getTime()
  })

  const renderRow = (workOrder: WorkOrder) => {
    const relatedTags = (Array.isArray(tags) ? tags : []).filter(
      (tag) => tag.workorder === Number(workOrder.id),
    )

    return (
      <>
        <tr className={css(styles.row)} key={workOrder.id}>
          {/* Regular work order row cells */}
        </tr>
        {relatedTags.length > 0 && (
          <tr>
            <td colSpan={columns.length}>
              <div className={css(styles.subComponent)}>
                {relatedTags.map((tag) => (
                  <div key={tag.id} className={css(styles.tagRow)}>
                    <div className={css(styles.tagInfo)}>
                      <span className={css(styles.tagLabel)}>{tag.label}</span>
                      <span className={css(styles.tagTitle)}>{tag.title}</span>
                      <span className={css(styles.tagStatus)}>
                        {tag.status}
                      </span>
                      <span className={css(styles.tagDriver)}>
                        {tag.assigned_to?.contact_object?.name || 'Unassigned'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </td>
          </tr>
        )}
      </>
    )
  }

  const handleComplete = async (workOrderId: number) => {
    try {
      await workOrderService.markAsComplete(workOrderId)

      // Mark work order notification as read when completed
      try {
        await workOrderNotifications.markAsRead(workOrderId)

        // Refresh notifications to update split counters (pending/accepted counts)
        await refreshNotifications()
      } catch (error) {
        console.error('Failed to mark work order notification as read:', error)
        // Don't show error to user as this is not critical
      }

      // Refresh the work orders list
      const updated = await workOrderService.getWorkOrders({
        page: 1,
        page_size: pageSize,
      })
      setWorkOrders(updated.items)
      toast.success('Work order marked as complete')
    } catch (error) {
      console.error('Failed to mark work order as complete:', error)
      toast.error('Failed to mark work order as complete')
    }
  }

  // Check if user can accept work orders (only SUB_HAULER stakeholder type)
  const canAcceptWorkOrders =
    user?.stakeholder_types?.includes(StakeholderType.SUB_HAULER) || false

  // Check if user is a site supervisor
  const isSiteSupervisor = user?.user_type === UserType.SITE_SUPERVISOR

  // Check if user is a contractor
  const handleViewWorkOrder = (workOrder: WorkOrder) => {
    // Show work order in view-only mode
    setSelectedViewWorkOrder(workOrder)
  }

  const handleSearch = () => {
    setActiveSearch(searchValue)
  }

  const handleSearchClear = () => {
    setSearchValue('')
    setActiveSearch('')
  }

  const desktopColumns = [
    columnHelper.display({
      id: 'actions',
      header: () => <span>ACTIONS</span>,
      cell: ({ row }) => {
        const isPending =
          (currentTab === 'pending' &&
            (row.original.status === 'PENDING' ||
              row.original.status === 'NEW' ||
              !row.original.status)) ||
          isSiteSupervisor
        const isDraft = row.original.status === 'DRAFT'

        const actions = []

        if (isDraft) {
          // Draft: Show Continue Dispatch action
          actions.push({
            label: 'Continue Dispatch',
            icon: 'fa-solid fa-pen',
            onClick: () => handleViewWorkOrder(row.original),
            className: css(styles.viewButton),
          })
        } else if (isPending) {
          if (canAcceptWorkOrders && !isSiteSupervisor) {
            actions.push({
              label: 'Accept Dispatch',
              icon: 'fa-solid fa-check',
              onClick: () => setSelectedAcceptWorkOrder(row.original),
              className: css(styles.acceptButton),
            })
          } else {
            actions.push({
              label: 'View Dispatch – Pending',
              icon: 'fa-solid fa-eye',
              onClick: () => handleViewWorkOrder(row.original),
              className: css(styles.viewButton),
            })
          }
        } else {
          // Accepted or Completed
          if (row.original.status !== 'COMPLETE') {
            // Ensure View is second: New Tag (1st), View (2nd), Set Complete (3rd)
            actions.push({
              label: 'New Tag',
              icon: 'fa-solid fa-tag',
              onClick: () => {
                // Clear any existing draft when starting fresh
                localStorage.removeItem(TAG_DRAFT_KEY)
                setTagModalWorkOrder(row.original)
              },
              className: css(styles.tagButton),
            })

            actions.push({
              label: 'View Dispatch – Accepted',
              icon: 'fa-solid fa-eye',
              onClick: () => handleViewWorkOrder(row.original),
              className: css(styles.viewButton),
            })

            actions.push({
              label: 'Set Complete',
              icon: 'fa-solid fa-check',
              onClick: () =>
                row.original.id && handleComplete(Number(row.original.id)),
              className: css(styles.completeButton),
            })
          } else {
            // Completed: Only View option available
            actions.push({
              label: 'View Dispatch – Completed',
              icon: 'fa-solid fa-eye',
              onClick: () => handleViewWorkOrder(row.original),
              className: css(styles.viewButton),
            })
          }
        }

        return <ActionMenu actions={actions} />
      },
    }),
    columnHelper.accessor('label', {
      header: () => <span>DISPATCH ID</span>,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('project', {
      header: 'PROJECT ID',
      cell: (info) => {
        const project = info.getValue()
        if (typeof project === 'object') {
          return project.label || project.job_site || '-'
        }
        const projectId = String(project)
        return projectLabels[projectId] || projectId
      },
    }),
    columnHelper.accessor('starting_date', {
      cell: (info) => formatDateTimeFromString(info.getValue()),
      header: () => <span>START DATE</span>,
    }),
    columnHelper.accessor('completion_date', {
      cell: (info) => formatDate(info.getValue() || null),
      header: () => <span>COMPLETION DATE</span>,
    }),
    columnHelper.accessor('pickup_address', {
      cell: (info) =>
        info.getValue() ? (
          <OpenMap
            address={info.getValue()}
            className={css(styles.addressCell)}
          />
        ) : (
          <span className={css(styles.addressCell)}>-</span>
        ),
      header: () => <span>PICK UP LOCATION</span>,
    }),
    columnHelper.accessor('dropoff_address', {
      cell: (info) =>
        info.getValue() ? (
          <OpenMap
            address={info.getValue()}
            className={css(styles.addressCell)}
          />
        ) : (
          <span className={css(styles.addressCell)}>-</span>
        ),
      header: () => <span>DROP OFF LOCATION</span>,
    }),

    columnHelper.display({
      id: 'subhaulers',
      header: 'Sub Hauler Corp.',
      cell: ({ row }) => {
        const workOrder = row.original
        // NEW: Display multiple subhaulers with contact info
        if (workOrder.subhaulers && workOrder.subhaulers.length > 0) {
          return (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              {workOrder.subhaulers.map((user, index) => {
                const displayName =
                  `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                  user.email ||
                  'Unknown'
                return (
                  <div
                    key={user.id || index}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                    }}
                  >
                    <PhoneContact
                      displayName={displayName}
                      phoneNumber={user.phone_number}
                    />
                    {user.email && (
                      <a
                        href={`mailto:${user.email}`}
                        style={{
                          color: '#6B7280',
                          fontSize: '11px',
                          textDecoration: 'none',
                        }}
                      >
                        {user.email}
                      </a>
                    )}
                  </div>
                )
              })}
            </div>
          )
        }
        // Legacy fallback removed - new API uses subhaulers[] array
        return 'Unassigned'
      },
    }),
    columnHelper.display({
      id: 'dispatchers',
      header: 'Dispatchers',
      cell: ({ row }) => {
        const workOrder = row.original
        // NEW: Display multiple dispatchers with contact info
        if (workOrder.dispatchers && workOrder.dispatchers.length > 0) {
          return (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              {workOrder.dispatchers.map((user, index) => {
                const displayName =
                  `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                  user.email ||
                  'Unknown'
                return (
                  <div
                    key={user.id || index}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                    }}
                  >
                    <PhoneContact
                      displayName={displayName}
                      phoneNumber={user.phone_number}
                    />
                    {user.email && (
                      <a
                        href={`mailto:${user.email}`}
                        style={{
                          color: '#6B7280',
                          fontSize: '11px',
                          textDecoration: 'none',
                        }}
                      >
                        {user.email}
                      </a>
                    )}
                  </div>
                )
              })}
            </div>
          )
        }
        // Legacy fallback removed - new API uses dispatchers[] array
        return 'Unassigned'
      },
    }),
    columnHelper.display({
      id: 'broker',
      header: 'BROKER',
      cell: ({ row }) => {
        const workOrder = row.original
        // Get broker info from project if it's an object
        if (typeof workOrder.project === 'object' && workOrder.project) {
          const project = workOrder.project
          if (project.brokers && project.brokers.length > 0) {
            return (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                {project.brokers.map((user, index) => {
                  const displayName =
                    `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                    user.email ||
                    'Unknown'
                  return (
                    <div
                      key={user.id || index}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                      }}
                    >
                      <PhoneContact
                        displayName={displayName}
                        phoneNumber={user.phone_number}
                      />
                      {user.email && (
                        <a
                          href={`mailto:${user.email}`}
                          style={{
                            color: '#6B7280',
                            fontSize: '11px',
                            textDecoration: 'none',
                          }}
                        >
                          {user.email}
                        </a>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          }
          if (project.broker_preusers && project.broker_preusers.length > 0) {
            return project.broker_preusers
              .map((preuser) => preuser.name || preuser.email || 'Unknown')
              .join(', ')
          }
        }
        return '-'
      },
    }),
    columnHelper.display({
      id: 'thirdPartyBroker',
      header: 'THIRD-PARTY BROKER',
      cell: ({ row }) => {
        const workOrder = row.original
        // Get third-party broker info from project if it's an object
        if (typeof workOrder.project === 'object' && workOrder.project) {
          const project = workOrder.project
          if (
            project.third_party_brokers &&
            project.third_party_brokers.length > 0
          ) {
            return (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                {project.third_party_brokers.map((user, index) => {
                  const displayName =
                    `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                    user.email ||
                    'Unknown'
                  return (
                    <div
                      key={user.id || index}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                      }}
                    >
                      <PhoneContact
                        displayName={displayName}
                        phoneNumber={user.phone_number}
                      />
                      {user.email && (
                        <a
                          href={`mailto:${user.email}`}
                          style={{
                            color: '#6B7280',
                            fontSize: '11px',
                            textDecoration: 'none',
                          }}
                        >
                          {user.email}
                        </a>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          }
          if (
            project.third_party_broker_preusers &&
            project.third_party_broker_preusers.length > 0
          ) {
            return project.third_party_broker_preusers
              .map((preuser) => preuser.name || preuser.email || 'Unknown')
              .join(', ')
          }
        }
        return '-'
      },
    }),
    columnHelper.accessor('trucks_number', {
      cell: (info) => info.getValue() || '-',
      header: () => <span>NUMBER OF TRUCKS</span>,
    }),
    columnHelper.accessor('truck_types', {
      cell: (info) => (
        <div className={css(styles.truckTypesCell)}>
          {(info.getValue() || [])
            .map((type) => getTruckTypeLabel(type))
            .join(', ')}
        </div>
      ),
      header: () => <span>TRUCK TYPES</span>,
    }),
    columnHelper.accessor('material', {
      cell: (info) => String(info.getValue() || ''),
      header: () => <span>MATERIAL</span>,
    }),
    // Show rate type instead of pay rate
    columnHelper.accessor('rate_type', {
      cell: (info) => String(info.getValue() || '-'),
      header: () => <span>RATE TYPE</span>,
    }),
    columnHelper.accessor('notes', {
      cell: (info) => <NotesPreview note={info.getValue()} />,
      header: () => <span>NOTES</span>,
    }),
  ]

  const mobileColumns = [
    columnHelper.display({
      id: 'actions',
      header: () => <span>ACTIONS</span>,
      cell: ({ row }) => {
        const isPending =
          (currentTab === 'pending' &&
            (row.original.status === 'PENDING' ||
              row.original.status === 'NEW' ||
              !row.original.status)) ||
          isSiteSupervisor
        const isDraft = row.original.status === 'DRAFT'
        const actions = [] as {
          label: string
          icon: string
          onClick: () => void
          className?: string
        }[]
        if (isDraft) {
          // Draft: Show Continue Dispatch action
          actions.push({
            label: 'Continue',
            icon: 'fa-solid fa-pen',
            onClick: () => handleViewWorkOrder(row.original),
            className: css(styles.viewButton),
          })
        } else if (isPending) {
          if (canAcceptWorkOrders && !isSiteSupervisor) {
            actions.push({
              label: 'Accept',
              icon: 'fa-solid fa-check',
              onClick: () => setSelectedAcceptWorkOrder(row.original),
              className: css(styles.acceptButton),
            })
          } else {
            actions.push({
              label: 'View',
              icon: 'fa-solid fa-eye',
              onClick: () => handleViewWorkOrder(row.original),
              className: css(styles.viewButton),
            })
          }
        } else {
          if (row.original.status !== 'COMPLETE') {
            actions.push({
              label: 'Tag',
              icon: 'fa-solid fa-tag',
              onClick: () => {
                // Clear any existing draft when starting fresh
                localStorage.removeItem(TAG_DRAFT_KEY)
                setTagModalWorkOrder(row.original)
              },
              className: css(styles.tagButton),
            })
            actions.push({
              label: 'View',
              icon: 'fa-solid fa-eye',
              onClick: () => handleViewWorkOrder(row.original),
              className: css(styles.viewButton),
            })
          } else {
            actions.push({
              label: 'View',
              icon: 'fa-solid fa-eye',
              onClick: () => handleViewWorkOrder(row.original),
              className: css(styles.viewButton),
            })
          }
        }
        return <ActionMenu actions={actions} />
      },
    }),
    columnHelper.accessor('label', {
      header: () => <span>ID</span>,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('project', {
      header: 'PROJECT',
      cell: (info) => {
        const project = info.getValue()
        if (typeof project === 'object') {
          return project.label || project.job_site || '-'
        }
        const projectId = String(project)
        return projectLabels[projectId] || projectId
      },
    }),
    columnHelper.accessor('starting_date', {
      cell: (info) => formatDateTimeFromString(info.getValue()),
      header: () => <span>START</span>,
    }),
    columnHelper.accessor('material', {
      cell: (info) => String(info.getValue() || ''),
      header: () => <span>MATERIAL</span>,
    }),
    columnHelper.accessor('notes', {
      cell: (info) => <NotesPreview note={info.getValue()} />,
      header: () => <span>NOTES</span>,
    }),
  ]

  const columns = desktopColumns

  const renderSubRow = useCallback(
    (workOrder: WorkOrder) => {
      return (
        <TagSubRow
          tags={tags}
          workOrderId={workOrder.id?.toString() || ''}
          columnsCount={columns.length}
          isReportMode={isReportMode}
          selectedTags={selectedTags}
          onTagToggle={handleTagToggle}
        />
      )
    },
    [tags, columns.length, isReportMode, selectedTags, handleTagToggle],
  )

  return (
    <div className={css(styles.workOrderDashboard)}>
      {tagModalWorkOrder && (
        <AddTagModal
          workorder={{ id: String(tagModalWorkOrder.id), ...tagModalWorkOrder }}
          onClose={() => setTagModalWorkOrder(null)}
        />
      )}
      {selectedAcceptWorkOrder && (
        <AcceptWorkOrderModal
          workOrder={selectedAcceptWorkOrder}
          onClose={() => setSelectedAcceptWorkOrder(null)}
          onSuccess={refreshWorkOrders}
        />
      )}
      {selectedViewWorkOrder && (
        <ViewWorkOrderModal
          workOrder={selectedViewWorkOrder}
          onClose={() => setSelectedViewWorkOrder(null)}
        />
      )}
      {showStatusReportConfirmation && (
        <StatusReportConfirmationModal
          isOpen={showStatusReportConfirmation}
          completedTags={selectedTagsForReport}
          onClose={() => setShowStatusReportConfirmation(false)}
          onConfirm={handleConfirmStatusReport}
          suggestedRecipientIds={[
            ...new Set(
              selectedTagsForReport
                .filter((t) => t.contractor_user?.id)
                .map((t) => t.contractor_user?.id),
            ),
          ]}
        />
      )}

      {/* New Dispatch Flow - Step 1: Project Selector */}
      {showProjectSelector && (
        <EntitySelectorModal
          title="Select Project Terms"
          description="Choose an accepted project terms to create a dispatch for:"
          entities={acceptedProjects}
          isLoading={isLoadingProjects}
          onClose={() => setShowProjectSelector(false)}
          onSelect={handleProjectSelect}
          renderItem={(project, onClick) => (
            <ProjectSelectorItem project={project} onClick={onClick} />
          )}
          getEntityId={(project) => project.id}
          searchPlaceholder="Search by label, job site, material, PO, parties..."
          searchValue={projectSearchValue}
          onSearchChange={handleProjectSearchChange}
          emptyMessage="No accepted project terms found. Please accept a project terms first."
          page={projectSelectorPage}
          totalPages={projectSelectorTotalPages}
          totalCount={projectSelectorTotalCount}
          onPageChange={handleProjectPageChange}
        />
      )}

      {/* New Dispatch Flow - Step 2: Template Selector */}
      {pendingDispatchProject && !selectedDispatchTemplateType && (
        <DispatchTemplateSelectorModal
          onSelect={handleTemplateTypeSelect}
          onClose={() => setPendingDispatchProject(null)}
        />
      )}

      {/* New Dispatch Flow - Step 3: Add Work Order Modal */}
      {pendingDispatchProject && selectedDispatchTemplateType && (
        <AddWorkOrderModal
          project={pendingDispatchProject}
          templateRecipientType={selectedDispatchTemplateType}
          onClose={handleCloseDispatchFlow}
        />
      )}

      <div className={css(styles.mainContent)}>
        <div className={css(styles.header)}>
          <div className={css(styles.topRow)}>
            <div className={css(styles.titleSection)}>
              <h2 className={css(styles.title)}>
                Dispatches{' '}
                {currentTab === 'drafts'
                  ? '- Draft'
                  : currentTab === 'pending'
                    ? '- Pending'
                    : currentTab === 'declined'
                      ? '- Declined'
                      : currentTab === 'completed'
                        ? '- Completed'
                        : '- Accepted'}
              </h2>
            </div>

            {!isReportMode && (
              <div className={css(styles.actionButtons)}>
                {currentTab === 'drafts' || currentTab === 'pending' ? (
                  <button
                    className={css(styles.newDispatchButton)}
                    onClick={handleNewDispatch}
                  >
                    <i className="fa-solid fa-plus" /> New Dispatch
                  </button>
                ) : (
                  <button
                    className={css(styles.generateReportButton)}
                    onClick={handleGenerateReport}
                  >
                    Generate Report
                  </button>
                )}
              </div>
            )}

            {isReportMode && (
              <div className={css(styles.reportModeActions)}>
                <span className={css(styles.selectedCount)}>
                  {selectedTags.size} tag{selectedTags.size !== 1 ? 's' : ''}{' '}
                  selected
                </span>
                <button
                  className={css(
                    styles.submitReportButton,
                    selectedTags.size === 0 &&
                      styles.submitReportButtonDisabled,
                  )}
                  onClick={handleSubmitReport}
                  disabled={selectedTags.size === 0}
                >
                  View Report
                </button>
                <button
                  className={css(styles.cancelReportButton)}
                  onClick={handleCancelReport}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div className={css(styles.filtersRow)}>
            <DateRangeFilter
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={setDateFrom}
              onDateToChange={setDateTo}
              onClear={() => {
                setDateFrom(null)
                setDateTo(null)
              }}
              showYearMonthDropdowns={true}
            />
            <SearchBar
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              onSearch={handleSearch}
              onClear={handleSearchClear}
              placeholder="Search materials, addresses, and more..."
            />
          </div>
        </div>

        <div className={css(styles.tableContainer)}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className={css(styles.tableWrapper)}
            >
              {isLoading ? (
                <SkeletonLoader
                  rows={8}
                  columns={7}
                  showHeader={true}
                  className={css(styles.skeletonLoader)}
                />
              ) : (
                <Table
                  data={sortedWorkOrders}
                  columns={columns}
                  renderSubRow={renderSubRow}
                  isReportMode={isReportMode}
                  selectedItems={selectedWorkOrders}
                  onItemToggle={handleWorkOrderToggle}
                  getItemId={(workOrder) => workOrder.id?.toString() || ''}
                  page={page}
                  pages={totalPages}
                  count={totalCount}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size)
                    setPage(1)
                  }}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  workOrderDashboard: {
    display: 'flex',
    padding: '16px',
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
    width: 'calc(100vw - 280px)',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    '@media (max-width: 1024px)': {
      padding: '12px',
      width: '100vw',
    },
  },
  mainContent: {
    flex: 1,
    width: '100%',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '20px',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '16px',
      padding: '12px',
    },
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
  },
  filtersRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gap: '16px',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '12px',
    },
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  generateReportButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#1955CC',
    },
  },
  newDispatchButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  reportModeActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  selectedCount: {
    fontSize: '14px',
    color: '#6B7280',
    fontWeight: 500,
  },
  submitReportButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#1955CC',
    },
  },
  submitReportButtonDisabled: {
    backgroundColor: '#D1D5DB',
    color: '#9CA3AF',
    cursor: 'not-allowed',
    opacity: 0.6,
    '&:hover': {
      backgroundColor: '#D1D5DB',
    },
  },
  cancelReportButton: {
    backgroundColor: '#6B7280',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#4B5563',
    },
  },
  tableContainer: {
    borderRadius: '8px',
    padding: '16px',
    width: '100%',
    boxSizing: 'border-box',
    marginTop: '-20px',
    overflowX: 'hidden',
  },
  tableWrapper: {
    width: '100%',
    position: 'relative',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    alignItems: 'flex-start',
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  tagButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    fontWeight: 500,
    minWidth: '120px',
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  acceptButton: {
    backgroundColor: '#10B981',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    fontWeight: 500,
    minWidth: '120px',
    ':hover': {
      backgroundColor: '#059669',
    },
  },
  viewButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    fontWeight: 500,
    minWidth: '120px',
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  subComponent: {
    padding: '8px 0 8px 40px',
    backgroundColor: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
  },
  tagRow: {
    padding: '8px 16px',
    borderLeft: '2px solid #1C64F2',
    marginBottom: '4px',
    backgroundColor: 'white',
    borderRadius: '4px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  tagInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    fontSize: '13px',
  },
  tagLabel: {
    fontWeight: 600,
    color: '#1C64F2',
    minWidth: '80px',
  },
  tagTitle: {
    color: '#374151',
  },
  tagStatus: {
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: '#E5E7EB',
    fontSize: '12px',
    fontWeight: 500,
  },
  tagDriver: {
    color: '#6B7280',
  },
  row: {
    // Add appropriate styles for the row
  },
  truckTypesCell: {
    maxWidth: '200px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  addressCell: {
    maxWidth: '250px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '13px',
    lineHeight: '1.4',
  },
  completeButton: {
    backgroundColor: '#10B981',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    fontWeight: 500,
    minWidth: '120px',
    ':hover': {
      backgroundColor: '#059669',
    },
  },
  completedBadge: {
    backgroundColor: '#10B981',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'default',
    minWidth: '120px',
  },
  reportButton: {
    backgroundColor: '#7C3AED',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    fontWeight: 500,
    minWidth: '120px',
    ':hover': {
      backgroundColor: '#6D28D9',
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#6B7280',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '16px',
  },
  skeletonLoader: {
    margin: '0',
    border: 'none',
    borderRadius: '8px',
  },
})

export default WorkOrderDashboard
